import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import SvgIcon from '../ui-core/svg-icon';

const progressAnimation = keyframes`
  0% {
    left: -50%;
  }
  100% {
    left: 100%;
  }
`;

const PageWrapper = styled.main`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 24px;
  background-color: ${({ theme }) => theme?.background?.default || '#f4f4f4'};
  color: ${({ theme }) => theme?.text?.default || '#333'};
  font-family: ${({ theme }) => theme?.typography?.fontFamily?.primary || "'IBM Plex Sans', sans-serif"};
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
`;

const Container = styled.div`
  max-width: 420px;
  text-align: center;
`;

const TrafficLight = styled.div`
  width: 80px;
  background-color: #222;
  border-radius: 10px;
  padding: 10px;
  display: inline-block;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
`;

const Light = styled.div`
  width: 60px;
  height: 60px;
  margin: 10px auto;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? '#e62e2e' : '#555')};
  box-shadow: ${({ $active }) => ($active ? '0 0 20px 5px rgba(230, 46, 46, 0.6)' : 'none')};
`;

const Heading = styled.h1`
  margin: 24px 0 12px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
`;

const Text = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme?.text?.subtle || '#666'};
`;

const ProgressTrack = styled.div`
  width: min(300px, 100%);
  height: 10px;
  background: ${({ theme }) => theme?.background?.subtle || '#ddd'};
  margin: 24px auto 0;
  border-radius: 5px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 50%;
    background: linear-gradient(90deg, #ff3e3e, #e62e2e);
    animation: ${progressAnimation} 2s infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
      left: 25%;
    }
  }
`;

/**
 * Full-page maintenance state shown when LaunchDarkly flag
 * `enable-under-maintenance` is on.
 */
const UnderConstruction = ({
    title = 'Under Maintenance',
    description = 'The portal is temporarily unavailable while we perform updates. Please check back soon.',
}) => (
    <PageWrapper role="main" aria-labelledby="maintenance-heading">
        <LogoContainer aria-hidden="true">
            <SvgIcon type="logo-primary" />
        </LogoContainer>
        <Container>
            <TrafficLight aria-hidden="true">
                <Light $active />
                <Light />
                <Light />
            </TrafficLight>
            <Heading id="maintenance-heading">{title}</Heading>
            <Text aria-live="polite">{description}</Text>
            <ProgressTrack aria-hidden="true" />
        </Container>
    </PageWrapper>
);

UnderConstruction.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
};

export default UnderConstruction;
